import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import type { Theme } from './index.ts';

/* ---------------- Theme: Tide (default) / Arctic. Stored per browser; the html attribute drives every variable. */
const THEME_KEY = 'caios.theme';
const ThemeCtx = createContext<{ theme: Theme; setTheme: (t: Theme) => void }>({ theme: 'tide', setTheme: () => {} });
export function readTheme(): Theme {
  try { return localStorage.getItem(THEME_KEY) === 'arctic' ? 'arctic' : 'tide'; } catch { return 'tide'; }
}
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, set] = useState<Theme>(readTheme);
  useEffect(() => { document.documentElement.dataset.theme = theme; }, [theme]);
  const setTheme = useCallback((t: Theme) => { try { localStorage.setItem(THEME_KEY, t); } catch { /* private window */ } set(t); }, []);
  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}
export const useTheme = () => useContext(ThemeCtx);

export function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="cx-seg" role="group" aria-label="Theme">
      {(['tide', 'arctic'] as const).map((t) => (
        <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)} data-testid={`theme-${t}`}>{t}</button>
      ))}
    </div>
  );
}

/* ---------------- Tool icons: the locked files, picked by theme (ICON-SPEC §1b). Never recoloured at runtime. */
export function ToolIcon({ name, size = 30 }: { name: string; size?: number }) {
  const { theme } = useTheme();
  return <img src={`/${theme === 'arctic' ? 'tools-arctic' : 'tools'}/${name}.svg`} alt="" width={size} height={size} />;
}

/* ---------------- Fields */
export function Field({ label, children, fit, hint }: { label: string; children: ReactNode; fit?: boolean; hint?: string | undefined }) {
  return (
    <label className={`cx-field${fit ? ' cx-field--fit' : ''}`}>
      <span className="cx-label">{label}</span>
      {children}
      {hint && <span className="cx-label" style={{ textTransform: 'none', letterSpacing: 0, fontFamily: 'var(--cx-font-sans)', fontSize: 12 }}>{hint}</span>}
    </label>
  );
}
export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={`cx-input ${p.className ?? ''}`} />;
export const TextArea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea {...p} className={`cx-textarea ${p.className ?? ''}`} />;
export const DateInput = (p: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) => <Input {...p} type="date" />;
export const TimeInput = (p: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) => <Input {...p} type="time" step={p.step ?? 900} />;
export function Select({ children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <span className="cx-select-wrap"><select {...p} className={`cx-select ${p.className ?? ''}`}>{children}</select></span>;
}
export function Checkbox({ label, small, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: ReactNode; small?: boolean }) {
  return <label className={`cx-check${small ? ' cx-check--sm' : ''}`}><input type="checkbox" {...p} />{label}</label>;
}
export function Radio({ label, small, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: ReactNode; small?: boolean }) {
  return <label className={`cx-check${small ? ' cx-check--sm' : ''}`}><input type="radio" {...p} />{label}</label>;
}

/* ---------------- Tabs (one level inside a workspace) */
export function Tabs<T extends string>({ value, onChange, items, testid = 'tab' }: { value: T; onChange: (v: T) => void; items: { id: T; label: string; count?: number | undefined }[]; testid?: string }) {
  return (
    <div className="cx-tabs" role="tablist">
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" className="cx-tab" aria-selected={value === t.id} onClick={() => onChange(t.id)} data-testid={`${testid}-${t.id}`}>
          {t.label}{t.count !== undefined && <span className="cx-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Empty state: says what's missing and the one thing to do about it */
export function Empty({ icon, title, children, action }: { icon?: string; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="cx-empty cx-rise">
      {icon && <ToolIcon name={icon} size={32} />}
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/* ---------------- Toast: "says what happened and where" (brand kit). useToast()('Booked <b>Intro call</b> · Scheduling') */
type ToastItem = { id: number; node: ReactNode; tone: 'ok' | 'fire' };
const ToastCtx = createContext<(node: ReactNode, tone?: 'ok' | 'fire') => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const push = useCallback((node: ReactNode, tone: 'ok' | 'fire' = 'ok') => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-2), { id, node, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 2700);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="cx-toasts" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`cx-toast${t.tone === 'fire' ? ' cx-toast--fire' : ''}`} data-testid="toast">{t.node}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/* ---------------- Count-up: ease-out-cubic over 1.2 s on first show; later changes tick (brand kit). */
export function CountUp({ value, format = (n) => Math.round(n).toLocaleString() }: { value: number; format?: (n: number) => string }) {
  const [shown, setShown] = useState(0);
  const [tick, setTick] = useState(0);
  const first = useRef(true);
  useEffect(() => {
    const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!first.current || reduce) { setShown(value); setTick((t) => t + 1); return; }
    first.current = false;
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => { const p = Math.min(1, (t - t0) / 1200); setShown(value * (1 - (1 - p) ** 3)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span key={tick} className={`cx-num${tick > 1 ? ' cx-tick' : ''}`}>{format(shown)}</span>;
}

/* ---------------- Leave / fold: play the exit, then run the action's follow-up (refetch). */
export function useExit(ms = 400) {
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const exit = useCallback(async (id: string, then: () => unknown) => {
    setLeaving((s) => new Set(s).add(id));
    await new Promise((r) => setTimeout(r, ms));
    await then();
    setLeaving((s) => { const n = new Set(s); n.delete(id); return n; });
  }, [ms]);
  return { leaving: (id: string) => leaving.has(id), exit };
}

/* ---------------- Hover card for rail items (fixed-position so the rail's overflow can't clip it) */
export function useHoverCard() {
  const [card, setCard] = useState<{ title: string; text: string; top: number; left: number } | null>(null);
  const id = useId();
  const show = (el: HTMLElement, title: string, text: string) => { const r = el.getBoundingClientRect(); setCard({ title, text, top: r.top + r.height / 2, left: r.right + 10 }); };
  const node = card ? <div className="cx-rail-card" role="tooltip" id={id} style={{ top: card.top, left: card.left }}><b>{card.title}</b><p>{card.text}</p></div> : null;
  return { show, hide: () => setCard(null), node };
}
